import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { Box, Tooltip, InfoIcon } from 'edenred-ui';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { slideoutMode } from '@epi-constants/slideoutMode';
import { ILimitSection } from '@epi-models/containers/ManageBenefitGroups';
import { LimitType } from '@epi-constants/benefitTypes';
import { getSettings } from '@epi-selectors/settings';
import { toComma } from '@epi-helpers/commaHelper';

import { ManageBenefitLunchFormFields } from '../../helpers';
import { DailyLimitField } from '../DailyLimitField';
import { LimitTypeField } from '../LimitTypeField';
import { useBenefitAmounts } from '@epi-containers/ManageBenefitGroups/hooks';
import { startCase } from 'lodash';

const Bold = styled.span`
  font-weight: bold;
`;

const Italic = styled.span`
  font-style: italic;
`;

export function LimitSection({ detail, benefitType }: ILimitSection) {
  const { primaryFrontColor, sizeMedium, fontSemiBold } = useTheme();
  const { t } = useTranslation();
  const settings = useSelector(getSettings);
  const { watch, setValue, clearErrors } = useFormContext();
  const [limitDisabled, setLimitDisabled] = useState(false);
  const benefitMaxAmounts = useBenefitAmounts();
  const isMaximumSpendingLimit = watch(
    ManageBenefitLunchFormFields.IsMaximumSpendingLimit
  );
  const limitType = watch(ManageBenefitLunchFormFields.IsMaximumSpendingLimit);
  useEffect(() => {
    if (isMaximumSpendingLimit === LimitType.maximum) {
      setValue(
        ManageBenefitLunchFormFields.DailyLimit,
        toComma(settings.lunchMaxAmount.toFixed(2))
      );
      setLimitDisabled(true);
    }
    if (isMaximumSpendingLimit === LimitType.custom) {
      setLimitDisabled(false);
    }
  }, [isMaximumSpendingLimit]);

  useEffect(() => {
    clearErrors(ManageBenefitLunchFormFields.DailyLimit);
  }, [limitType]);
  return (
    <>
      <Box pb={1} display="flex" alignItems="center">
        <Box
          fontSize={sizeMedium}
          fontWeight={fontSemiBold}
          id={'lounari-limit-section-title'}
        >
          {t('components.manage_benefits.spending_limit_field.title')}
        </Box>
        <Tooltip
          arrow
          placement="top"
          id="lounari-limit-section-tax-tooltip"
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: '500px'
              }
            }
          }}
          title={
            <Trans
              i18nKey={`components.manage_benefits.spending_limit_field.info`}
              components={{ bold: <Bold />, italic: <Italic /> }}
            />
          }
        >
          <Box pl={1} id="lounari-limit-section-tax-tooltip-icon">
            <InfoIcon color={primaryFrontColor} />
          </Box>
        </Tooltip>
      </Box>
      <LimitTypeField
        name={ManageBenefitLunchFormFields.IsMaximumSpendingLimit}
        disabled={detail.mode === slideoutMode.view}
      />
      <Box pb={2} display="flex">
        <DailyLimitField
          label={t(
            `components.manage_benefit_${benefitType}.detail_limit_label`
          )}
          readOnly={detail.mode === slideoutMode.view}
          benefitType={benefitType}
          disabled={limitDisabled}
          fundsLoadingTypeName={ManageBenefitLunchFormFields.FundsLoadingType}
          name={ManageBenefitLunchFormFields.DailyLimit}
          maxAmounts={benefitMaxAmounts[startCase(benefitType)]}
        />
        <Tooltip
          arrow
          placement="top"
          id="lounari-limit-section-limit-tooltip"
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: '500px'
              }
            },
            popper: {
              sx: {
                zIndex: 7000
              }
            }
          }}
          title={
            <Trans
              i18nKey={`components.manage_benefit_${benefitType}.detail_daily_limit_statement`}
            />
          }
        >
          <Box pl={1} pt="22px" id="lounari-limit-section-limit-tooltip-icon">
            <InfoIcon color={primaryFrontColor} />
          </Box>
        </Tooltip>
      </Box>
    </>
  );
}
